import { PTRDataStoreBlankItem } from '@app/data-stores/ptr/types';
import type { DeepReadonly } from '@app/types';
import type { InventoryItem, Work } from '@app/types/shop-bff/types';
import { getDiscountValue } from '@app/utils/common';
import type {
  PageProductDetails,
  RSCheckoutLocationKey,
  RSCheckoutStepKey,
  RSFilterClickedLocationKey,
  RSLinkClickedLocationKey,
  RSPageTypeKey,
  RSProductClickedLocationKey,
  RudderstackBrowserClient,
} from '@redbubble/next-provider/rudderstack';
import { getItemValue } from './utils';

export const buildPageProductDetails = (
  inventoryItem: InventoryItem,
  work: NonNullable<Work>,
  blankItem: PTRDataStoreBlankItem | undefined
): PageProductDetails => ({
  // product_id mirrors old productViewed: marketingProductTypeId (e.g. "52280794-US-sticker"), not inventoryItem.id
  product_id: String(inventoryItem.marketingProductTypeId),
  name: `${work.title} ${inventoryItem.description}`,
  price: inventoryItem.price?.amount,
  currency: inventoryItem.price?.currency,
  sku: inventoryItem.id,
  category: inventoryItem.description,
  image_url: inventoryItem.previewSet?.previews?.[0]?.url,
  url: String(inventoryItem.productPageUrl),
  product_type_id: inventoryItem.productTypeId,
  blank_id: inventoryItem.blankItemId,
  work_id: String(inventoryItem.workId),
  quantity: 1,
  legacy_product_details: buildLegacyProductOptions(blankItem?.options?.values),
  discount: getDiscountValue(inventoryItem.price),
  value: inventoryItem.price ? getItemValue(inventoryItem.price) : undefined,
  canvas: inventoryItem.gaCode ?? undefined,
  is_licensed: work.licensed ?? undefined,
  is_safe_for_work:
    work.matureContent != null ? !work.matureContent : undefined,
});

const buildLegacyProductOptions = (
  values: DeepReadonly<{ key: string; value: string }[]> | undefined
): Record<string, string> => {
  if (!values) return {};
  return Object.fromEntries(values.map(({ key, value }) => [key, value]));
};

interface IRudderstackTrack {
  rsClient?: RudderstackBrowserClient;
}

export const trackRSCheckoutStepSubmitted = ({
  rsClient,
  checkoutStep,
}: IRudderstackTrack & {
  checkoutStep: RSCheckoutStepKey;
}) => {
  if (!rsClient) return;

  rsClient.checkoutStepSubmitted({
    checkout_step: checkoutStep,
  });
};

export const trackRSCheckoutClicked = ({
  rsClient,
  checkoutType,
  location,
}: IRudderstackTrack & {
  checkoutType: string;
  location: RSCheckoutLocationKey;
}) => {
  if (!rsClient) return;

  const rsCheckoutTypeProperty = checkoutType.replaceAll('_', '-');

  rsClient.checkoutClicked({
    checkout_type: rsCheckoutTypeProperty,
    location: location,
  });
};

export const trackRSLinkClicked = ({
  rsClient,
  linkLabel,
  location,
}: IRudderstackTrack & {
  linkLabel: string;
  location: RSLinkClickedLocationKey;
}) => {
  if (!rsClient) return;

  rsClient.linkClicked({
    link_label: linkLabel,
    location,
  });
};

export const trackRSFilterClicked = ({
  rsClient,
  filterLabel,
  location,
}: IRudderstackTrack & {
  filterLabel: string;
  location: RSFilterClickedLocationKey;
}) => {
  if (!rsClient) return;

  rsClient.filterClicked({
    filter_label: filterLabel,
    location,
  });
};

export const trackRSProductClicked = ({
  rsClient,
  blankId: blank_id,
  brand,
  category,
  country,
  coupon,
  currency,
  imageUrl: image_url,
  legacyProductDetails: legacy_product_details,
  locale,
  location,
  name,
  pageType: page_type,
  position,
  price,
  productId: product_id,
  productTypeId: product_type_id,
  quantity,
  sku,
  url,
  variant,
  workId: work_id,
}: IRudderstackTrack & {
  blankId?: string;
  brand?: string;
  category?: string;
  country?: string;
  coupon?: string;
  currency?: string;
  imageUrl?: string;
  legacyProductDetails?: Record<string, any>;
  locale?: string;
  location: RSProductClickedLocationKey;
  name?: string;
  pageType: RSPageTypeKey;
  position?: number;
  price?: number;
  productId?: string;
  productTypeId?: string;
  quantity?: number;
  sku?: string;
  url?: string;
  variant?: string;
  workId?: number | string;
}) => {
  if (!rsClient) return;

  rsClient.productClicked({
    blank_id,
    brand,
    category,
    country,
    coupon,
    currency,
    image_url,
    legacy_product_details,
    locale,
    location,
    name,
    page_type,
    position,
    price,
    product_id,
    product_type_id,
    quantity,
    sku,
    url,
    variant,
    work_id,
  });
};
